import { Grid, Hidden, Menu, MenuItem, useMediaQuery } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import CssBaseline from '@material-ui/core/CssBaseline';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import BusinessIcon from '@material-ui/icons/Business';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import InboxIcon from '@material-ui/icons/Inbox';
import ListIcon from '@material-ui/icons/List';
import MenuIcon from '@material-ui/icons/Menu';
import PeopleIcon from '@material-ui/icons/People';
import { useKeycloak } from '@react-keycloak/web';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from './assets/logo-exportafacil.png';
import { usePageTitle } from './context/LayoutContext';
import { onAuthLogout } from './keycloak';

const drawerWidth = 240;

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex'
  },
  appBar: {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  menuButton: {
    marginRight: theme.spacing(2)
  },
  hide: {
    display: 'none'
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0
  },
  drawerPaper: {
    width: drawerWidth
  },
  drawerLogo: {
    width: '100%',
    padding: theme.spacing(0, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end'
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    marginLeft: -drawerWidth,
    overflowX: 'auto',
    [theme.breakpoints.down('sm')]: {
      padding: 0
    }
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginLeft: 0
  },
  title: {
    flexGrow: 1
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  profileButton: {
    border: '1px solid #E4E4E4',
    borderRadius: 8,
    padding: '4px 14px',
    // '&:hover': {
    //   backgroundColor: '#E8ECFA'
    // }
    // color: theme.palette.text.hint,
    '&:hover': {
      // color: theme.palette.primary.main,
      backgroundColor: 'rgba(255, 255, 255, 0.35)'
    }
  },
  username: {
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: 'bold'
  }
}));

export default function AppLayout(props) {
  const classes = useStyles();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [isMounted, setIsMounted] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const { pageTitle, setPageTitle } = usePageTitle();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const location = useLocation();
  const openUser = Boolean(anchorEl);

  const { keycloak } = useKeycloak();
  const gruposList = keycloak.tokenParsed.groups || [];
  const esExportador =
    gruposList.includes('GRUPO_EXPORTADOR') ||
    gruposList.includes('GR_REG_EMPRESA_V2') ||
    gruposList.includes('GR_REG_EMPRESA_V3');
  const esAdmision = gruposList.includes('GR_EXPORTAFACIL_ADMISION');
  const esOperaciones = gruposList.includes('GR_EXPORTAFACIL_OPERACIONES');
  const esLogistica = gruposList.includes('GR_EXPORTAFACIL_LOGISTICA');

  const menuItems = [];

  if (esOperaciones) {
    menuItems.push({
      path: '/enviar-tees',
      key: 'lista_formulario_tees',
      title: 'Enviar a DNIT',
      icon: <InboxIcon />
    });
  }

  if (esExportador && !esAdmision && !esOperaciones && !esLogistica) {
    menuItems.push(
      {
        path: '/formularios',
        key: 'lista_formulario',
        title: 'Mis Formularios',
        icon: <ListIcon />
      },
      {
        path: '/personas',
        key: 'lista_personas',
        title: 'Personas',
        icon: <PeopleIcon />
      },
      {
        path: '/importadores',
        key: 'lista_importadores',
        title: 'Importadores',
        icon: <BusinessIcon />
      }
    );
  } else {
    menuItems.push({
      path: '/formularios',
      key: 'lista_formulario',
      title: 'Formularios',
      icon: <ListIcon />
    });
  }

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = title => {
    setPageTitle(title);
  };

  const isSelected = path => {
    return location.pathname === path;
  };

  // Solo activar después de que el componente haya sido montado
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(
    () => {
      // Este efecto se ejecutará solo una vez después del primer renderizado
      if (isMounted) {
        setOpen(!isPhone);
      }
    },
    [isMounted, isPhone]
  );

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open
        })}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            className={clsx(classes.menuButton, open && classes.hide)}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap className={classes.title}>
            {pageTitle}
          </Typography>
          {keycloak.authenticated && (
            <>
              <div className={classes.actionsContainer}>
                <Grid container wrap={'nowrap'}>
                  <List
                    component="nav"
                    aria-label="Device settings"
                    disablePadding={true}
                  >
                    <ListItem
                      button
                      aria-haspopup="true"
                      aria-controls="lock-menu"
                      aria-label="when device is locked"
                      onClick={handleMenu}
                      classes={{ root: classes.profileButton }}
                    >
                      <Grid
                        container
                        direction={'row'}
                        wrap={'nowrap'}
                        alignItems={'center'}
                        spacing={2}
                      >
                        <Hidden smDown>
                          <Grid item container direction={'column'}>
                            <Grid item>
                              <Typography
                              // className={classes.username}
                              // color={'primary'}
                              // fontWeight="fontWeightLight"
                              >
                                {keycloak.idTokenParsed?.name}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Hidden>
                        <Grid item style={{ padding: 0 }}>
                          <ArrowDropDown style={{ marginTop: 5 }} />
                        </Grid>
                      </Grid>
                    </ListItem>
                  </List>
                </Grid>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                  }}
                  open={openUser}
                  onClose={handleClose}
                  elevation={1}
                  classes={{ paper: classes.menuWrapper }}
                >
                  <ListItem classes={{ root: classes.email }}>
                    <ListItemText
                      primary={keycloak.idTokenParsed.name}
                      secondary={keycloak.idTokenParsed.email}
                    />
                  </ListItem>
                  <Divider className={classes.divider} />
                  <MenuItem
                    classes={{ root: classes.menuItem }}
                    onClick={handleClose}
                    component={Link}
                    to="/perfil"
                  >
                    Perfil
                  </MenuItem>
                  <Divider className={classes.divider} />
                  <MenuItem
                    style={{ color: '#CF0000', justifyContent: 'center' }}
                    onClick={() => {
                      onAuthLogout();
                      keycloak.logout();
                    }}
                    classes={{ root: classes.menuItem }}
                  >
                    Cerrar sesión
                  </MenuItem>
                </Menu>
              </div>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="left"
        open={open}
        classes={{
          paper: classes.drawerPaper
        }}
      >
        <div className={classes.drawerHeader}>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </div>
        <div className={classes.drawerLogo}>
          <img width="100%" height="auto" src={logo} alt="logo" />
        </div>
        <Divider />
        <List>
          {menuItems.map(({ key, title, path, icon }) => (
            <ListItem
              button
              component={Link}
              to={path}
              key={key}
              selected={isSelected(path)}
              onClick={() => handleMenuItemClick(title)}
            >
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText primary={title} />
            </ListItem>
          ))}
        </List>
      </Drawer>
      <main
        className={clsx(classes.content, {
          [classes.contentShift]: open
        })}
      >
        <div className={classes.drawerHeader} />
        {props.children}
      </main>
    </div>
  );
}
